import React, {ReactElement, useState, useEffect} from 'react';
import {
  DialogLegacy as Dialog,
  DialogLegacyContent as DialogContent,
  DialogLegacyActions as DialogActions,
  Button,
  Alert
} from '@customink/pigment-react';
import {
  useGetStoreDetailsQuery,
  useGetOrderSummaryQuery
} from '../../api/storesApi';
import {
  useGroupOrderOption,
  useCampaignStatus,
  useAppDispatch,
  useAppSelector
} from '../../hooks';
import services from '../../api/remoteServices';
import {showGroupOrderStatusDialog} from '../../slices/appSlice';
import {selectAdmin} from '../../slices/userSlice';

type DialogProps = {
  storeSlug: string;
  campaignId: number;
  isMobile: boolean;
  ccoUrl: string;
};

const GroupOrderStatusDialog: React.FC<DialogProps> = ({
  storeSlug,
  campaignId,
  isMobile,
  ccoUrl
}: DialogProps): ReactElement => {
  const dispatch = useAppDispatch();
  const {isFetching: isDetailsFetching} = useGetStoreDetailsQuery(storeSlug);
  const {data: campaignOrderData, isFetching: isOrderFetching} =
    useGetOrderSummaryQuery({
      urlSlug: storeSlug,
      campaignId
    });
  const {isCampaignCompleted, isCampaignGoalMet, isCampaignCanceled} =
    useCampaignStatus();
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const groupOrderOption = useGroupOrderOption();
  const showDialog = useAppSelector(state => {
    return state.app.showGroupOrderStatusDialog;
  });

  const buyupPurchaseId =
    campaignOrderData?.purchases.find(purchase => purchase.hasBuyup)
      ?.secureId ?? '';

  const admin = useAppSelector(selectAdmin);
  const isBuyupFlagEnabled = campaignOrderData?.buyupEnabled;
  const isInkerCheckoutEnabled = campaignOrderData?.inkerCheckoutEnabled;
  const inkerCheckoutUrl = `${services.stores}/${storeSlug}/campaigns/${campaignId}/checkout`;
  const orderConfirmUrl = `${services.stores}/${storeSlug}/purchases/${buyupPurchaseId}`;

  useEffect(() => {
    if (showDialog && !isDetailsFetching && !isOrderFetching) {
      if (isCampaignCompleted) {
        dispatch(showGroupOrderStatusDialog(false));
        window.location.href = orderConfirmUrl;
        return null;
      }

      if (isCampaignGoalMet || isCampaignCanceled || !isBuyupFlagEnabled) {
        if (isInkerCheckoutEnabled && admin) {
          window.location.href = inkerCheckoutUrl;
        } else {
          dispatch(showGroupOrderStatusDialog(false));
          window.location.href = ccoUrl;
        }
      }

      return setStatusDialogOpen(true);
    }
    return null;
  }, [isDetailsFetching, isOrderFetching]);

  const handleDialogClose = () => {
    setStatusDialogOpen(false);
    dispatch(showGroupOrderStatusDialog(false));
  };

  return (
    showDialog &&
    groupOrderOption && (
      <Dialog
        className="groupStatusDialog"
        title={groupOrderOption.title}
        open={statusDialogOpen}
        onClose={handleDialogClose}
        maxWidth="md">
        <DialogContent>
          <Alert severity="error" className="dialogAlert">
            {groupOrderOption.message}
          </Alert>
        </DialogContent>
        <DialogActions
          className={`groupStatusDialog-actions${isMobile ? ' mobile' : ''}`}>
          {groupOrderOption.button}
          <Button
            size="large"
            type="submit"
            onClick={handleDialogClose}
            href={`${services.storefront}/${storeSlug}`}
            target="_blank"
            rel="noreferrer">
            Buy More Items
          </Button>
        </DialogActions>
      </Dialog>
    )
  );
};

export default GroupOrderStatusDialog;
